import {
  faInfo,
  faXmarkCircle
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import SkeletonGrid from '../../../../../components/SkeletonLoader/SkeletonGrid';
import { useEffect, useRef, useState } from 'react';
import { getSetType } from '../../../../myContent/utils/getSetType';
import { useLocation } from 'react-router';
import { fixSampleAnswerContent, getMcCorrectAnswer, parseHtml, removeCheckedFromCheckboxes, removeCheckedFromRadioButton, removedropContainerChilds, removeStyleFromCheckedNext, replaceCheckedRadiosWithTick } from '../../../../assignments/pages/MoreActions/utils/StandardPrintFunctions';

const QuestionPrintContainer = ({
  setName,
  displayTopValue,
  fontStyleClass,
  printOptions,
  isLoading,
  teacherName,
  questionsData,
  handleQuestionsSelect,
  handleClearAll,
  setContentHeights,
  hideDuplicate
}) => {
  const [duplicateStacks, setDuplicateStacks] = useState([]);
  const questionsContainerRef = useRef(null);
  const duplicateDivRef = useRef(null);
  const location = useLocation();
  const activeTab = location.pathname.toLowerCase().split('/mycontent/')[1]?.split('/')[0];
  const { languageId } = location.state;

  useEffect(() => {
    if(!duplicateStacks || duplicateStacks?.length === 0) return;

    if (duplicateDivRef.current) {
      const heights = questionsData.map(qs => {
        const items = duplicateDivRef.current.querySelector(`#dstack_itemId_${qs.questionId}`);
        if (!items) return { id: qs.questionId, height: 0 };
  
        return {
          id: qs.questionId,
          height: items.offsetHeight
        };
      });
  
      var header = questionsContainerRef.current.querySelector('#documentHeader');
      heights.push({
        id: 'header',
        height: header.offsetHeight + 50
      });
      setContentHeights(heights);
    }
  }, [duplicateStacks]);

  useEffect(() => {
    if (questionsData?.length === 0) return;
  
    let singleStack = questionsData?.map((item) => ({
      ...item
    }));
  
    setDuplicateStacks(singleStack);
  }, [printOptions, questionsData]);

  function fixContentAlignment(html, qtype) {
    if (!html) return html;
  
    const standardWidth = 150;
    const maxWidth = 450;
    const doc = parseHtml(html);
  
    const imgs = doc.querySelectorAll('img');
    const imgsCount = imgs.length;
  
    imgs.forEach(img => {
      img.removeAttribute('onload');
  
      if (printOptions?.twoColumn) {
        const isPartOfTableLayout = !!img.closest('table');
        const imgClosest = img.closest('td, th');
        const hasColspan = imgClosest && imgClosest.hasAttribute('colspan');

        if (isPartOfTableLayout && imgsCount > 1 && !hasColspan) {
          img.style.maxWidth = `${standardWidth}px`;
          img.style.removeProperty('height');
        } else {
          img.style.maxWidth = `${maxWidth}px`;
          img.style.removeProperty('height');
        }
      }

      img.removeAttribute('height');
      img.removeAttribute('width');
      img.style.removeProperty('height');
      img.style.removeProperty('width');

      if (img.closest('.resizable-img-wrapper')) return;

      const wrapper = doc.createElement('div');
      wrapper.id = 'resizable-img-wrapper';
      wrapper.className = 'resizable-img-wrapper';
      wrapper.style.maxWidth = `${maxWidth}px`;

      img.parentNode.insertBefore(wrapper, img);
      wrapper.appendChild(img);
    });
  
    const bookletTables = doc.querySelectorAll('table.booklet');
  
    bookletTables.forEach(table => {
      if (table.getAttribute('width'))
        table.removeAttribute('width');
  
      const innerTables = table.querySelectorAll('table');
      innerTables.forEach(div => {
        const styleWidthMatch = div.style.width?.match(/(\d+)/);
        const widthAttr = div.getAttribute('width');
        let actualWidth = null;
  
        if (styleWidthMatch) {
          actualWidth = parseInt(styleWidthMatch[1], 10);
        } else if (widthAttr && !isNaN(parseInt(widthAttr, 10))) {
          actualWidth = parseInt(widthAttr, 10);
        }
  
        if (actualWidth && actualWidth > maxWidth) {
          if (styleWidthMatch) div.style.removeProperty('width');
          if (widthAttr) div.removeAttribute('width');
        }
      });

      if(printOptions?.twoColumn)
      {
        var pages = table.querySelectorAll('div[id^="page"]');
        pages.forEach(div => {
          div.style.maxWidth = `${maxWidth}px`;
        });
      }
    });
  
    const tablesWithWidth = doc.querySelectorAll('table');
  
    tablesWithWidth.forEach(table => {
      if (table.getAttribute('width')) table.removeAttribute('width');

      if(printOptions?.twoColumn) {
        table.style.fontSize = '12px';
        table.width = `${maxWidth}px`;
      }
  
      const styleWidthMatch = table.style.width?.match(/(\d+)/);
      if (styleWidthMatch) table.style.removeProperty('width');
    });
      
    if(qtype == 4 || qtype ==5)
      removeStyleFromCheckedNext(doc);
  
    doc.querySelectorAll('a').forEach(a => {
      a.style.setProperty('color', 'blue', 'important');
    });
  
    doc.querySelectorAll('span').forEach(span => {
      if (span.hasAttribute('style')) {
        span.style.removeProperty('font-family');
        span.style.removeProperty('font-size');
      }
    });
  
    doc.querySelectorAll('font').forEach(font => {
      const parent = font.parentNode;
      while (font.firstChild) {
        parent.insertBefore(font.firstChild, font);
      }
      parent.removeChild(font);
    });

    doc.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.removeAttribute('disabled');
    });

    doc.querySelectorAll('input[type="radio"]').forEach(checkbox => {
      checkbox.removeAttribute('disabled');
    });

    doc.querySelectorAll('ul, ol').forEach(list => {
      list.style.marginLeft = '0';
    });
  
    let finalHtml = doc.body.innerHTML;
  
    if(!printOptions?.displayAnswers) 
    {
      finalHtml = removedropContainerChilds(finalHtml);
      if(qtype == 4 || qtype == 5){
        finalHtml = removeCheckedFromCheckboxes(finalHtml);
        finalHtml = removeCheckedFromRadioButton(finalHtml);
      }
    } else if (qtype == 4) {
      finalHtml = replaceCheckedRadiosWithTick(finalHtml);
    }
    return finalHtml;
  }

  const generateQuestion = (item) => {
    let newQuestionText = item.question?.question;
    if (item.type == 'FillIn') {
      var answers = item.question.fillInAnswers;
      if (answers) {
        answers.forEach((item, index) => {
          newQuestionText = newQuestionText?.replace(`~~${index + 1}~~`, '<span class=\'answer-box\'></span>');
        });
      }
    }

    return fixContentAlignment(newQuestionText, item.typeId);
  };

  const updateRenderedImages = () => {
    if (!questionsContainerRef.current) return;

    const imgs = questionsContainerRef.current.querySelectorAll('img');
    const standardWidth = 450;
    const wrapperPadding = 8;
    const compressionScale = 1 - (printOptions?.imageCompressionPercent || 0) / 100;
    const finalWidth = Math.max(standardWidth * compressionScale, 50);

    imgs.forEach(img => {
      const wrapper = img.closest('.resizable-img-wrapper');
      if (!wrapper) return;

      const isInTable = !!img.closest('table');
      wrapper.style.width = '';
      img.style.width = '';

      if (isInTable) {
        wrapper.style.maxWidth = `${finalWidth}px`;
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
      } else {
        const src = img.getAttribute('src') || '';
        const isGeometryMathImage = ['geometry', 'math', 'algebra', 'precalc'].some(keyword => src.toLowerCase().includes(keyword));

        if (!isGeometryMathImage) {
          wrapper.style.width = `${finalWidth - wrapperPadding}px`;
          wrapper.style.maxWidth = `${standardWidth}px`;

          img.style.width = '100%';
          img.style.height = 'auto';
        }
      }
    });
  };

  useEffect(() => {
    if(!printOptions?.imageCompressionPercent) return;
    
    updateRenderedImages();
  }, [printOptions?.imageCompressionPercent]);

  const RenderItem = (item, index, isDuplicate = false) => {
    return (
      <div key={index} className={`questionBlock check-items d-flex gap-2 py-2 mb-2 ${!item?.isChecked ? 'doNotPrint' : 'printData check-items-border'}`}>
        {!isDuplicate && <input
          type="checkbox"
          className="form-check-input checkboxes"
          name={`question${item.questionId}`}
          checked={!!item?.isChecked}
          onChange={handleQuestionsSelect}
        />}
        <div className={`mx-2 ${!item?.isChecked ? 'doNotPrint' : 'printData'}`}>
          <div className={`question-layout d-flex align-items-start justify-content-between text-black ${printOptions?.twoColumn ? 'p-0 zoom-9' : ''}`}
            key={`question-${item.questionId}${isDuplicate ? '-duplicate' : ''}`} style={{color: 'black'}}
            id={`question-${item.questionId}${isDuplicate ? '-duplicate' : ''}`}>
            <div id={'q' + item.questionNo} className='col-lg-12 text-break'>
              <div className='d-flex justify-content-between gap-3'>
                <div className={`d-flex w-100 ${item.layoutOptions?.includes('exclude') ? 'bg-castleGray mb-2' : ''}`}>
                  <div className='fw-semibold'>{item.questionNo}.&nbsp;&nbsp;</div>
                  {item.type === 'MC' && item.question?.question && (
                    printOptions?.displayAnswers ? (
                      <div className='fw-semibold'>
                    Answer <span className='text-decoration-underline mx-2'>{getMcCorrectAnswer(item.question?.mcAnswer)}</span>
                      </div>
                    ) : printOptions?.showAnswerSpace ? (
                      <div className='fw-semibold'>Answer _______ </div>
                    ) : null
                  )}
                </div>
              </div>
              <div>
                {item.question?.resources?.map((resource, index) => (
                  <div key={index} dangerouslySetInnerHTML={{ __html: fixContentAlignment(resource, item.typeId) }}></div>
                ))}
                <div>
                  {item.question?.question && (
                    <div dangerouslySetInnerHTML={{ __html: generateQuestion(item) }}></div>
                  )}
                  {item.blockedText?.length > 0 && (
                    <div
                      className="fw-bold text-danger"
                      dangerouslySetInnerHTML={{ __html: item.blockedText }}></div>
                  )}
                </div>
                {item.type === 'MC' && item.question.mcAnswer.filter(a => a.answer.trim() !== '').length > 1 && (
                  <div className="mcAnswer-content">
                    <ul>
                      {item.question.mcAnswer.map((answer, indexValue) => (
                        <li key={`ans${indexValue}`} dangerouslySetInnerHTML={{ __html: fixContentAlignment(answer.answer.replace(/<p>/g, '').replace(/<\/p>/g, ''), item.typeId) }}
                        />
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {(item.type == 'CR' && printOptions?.showAnswerSpace && item.question?.question && !printOptions?.displayAnswers) &&
                <>
                  <div className='fw-semibold'>Answer</div>
                  <div className='h-70 border border-secondary'></div>
                </>
              }
              {(item.type == 'CR' || item.type == 'FillIn') && item.question?.question && printOptions?.displayAnswers &&
                <>
                  <div className='fw-semibold'>Answer</div>
                  <div className='border border-secondary p-3'
                    dangerouslySetInnerHTML={{
                      __html: item.type == 'CR' ?
                        (item.question?.sampleAnswer ? fixContentAlignment(fixSampleAnswerContent(item.question?.sampleAnswer), item.typeId) : 'There is no Sample Answer for this Contructed Response Question')
                        : fixContentAlignment(item.question?.fillInAnswers?.join(', '), item.typeId)
                    }}>
                  </div>
                </>
              }
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="vocabulary-content-scroll">
      <div id="vocabulary-data">
        {isLoading ?
          <div className='print-content p-4'>
            <SkeletonGrid
              rowCount={2}
              colCount={2}
              className='mt-2' />
            <SkeletonGrid
              rowCount={4}
              colCount={1}
              className='p-3 h-150 mt-3'
            />
          </div>
          :
          <div className="print-content vocabulary-content-data">
            <div ref={questionsContainerRef} className={`${fontStyleClass} content-${printOptions?.fontSize}`}>
              <div id="documentHeader" className='name-slip border-0 mb-3'>
                <div className='d-flex justify-content-between'>
                  <div className="d-flex align-items-center row">
                    <div>
                      {printOptions?.showNameLine && (
                        <div>
                          <label className="form-label"><b>Name:</b></label>
                          <input className="input-name" disabled></input>
                        </div>
                      )}
                    </div>
                    <div>
                      {printOptions?.showTeacherName && (
                        <label className="form-label teacher-name">
                          <b>Teacher:</b>&nbsp;&nbsp;<span>{teacherName}</span>
                        </label>
                      )}
                    </div>
                  </div>
                  <div className="d-flex align-items-center row">
                    <label className="form-label d-flex justify-content-end">
                      <b>Set Name:</b>&nbsp;&nbsp;
                      {setName}
                    </label>
                    <label className="form-label d-flex justify-content-end">
                      <b>{getSetType(activeTab) + 's:'}</b>&nbsp;&nbsp;
                      {questionsData?.length}
                    </label>
                  </div>
                </div>
                {displayTopValue?.length > 0 && (
                  <h3 className={`mb-3 text-center content-${printOptions?.fontSize}`}>{displayTopValue}</h3>
                )}
              </div>
              <div className="name-slip py-2 row" id="checkData">
                <div className="col-lg-6 d-flex align-items-center gap-3">
                  <div className="d-flex gap-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="selectAll"
                      checked={questionsData?.every((item) => item.isChecked)}
                      onChange={handleQuestionsSelect}
                    />{' '}
                    <label className="form-label mb-0">Check All</label>
                  </div>
                  <div className="d-flex align-items-center">
                    <button className="clear-btn" name="clearAll" onClick={handleClearAll}>
                      <FontAwesomeIcon icon={faXmarkCircle} className="close-mark" />
                        Clear All
                    </button>
                  </div>
                </div>
                <div className="col-lg-6 d-flex flex-row-reverse">
                  <div className="d-flex gap-1 align-items-center info-box">
                    <FontAwesomeIcon icon={faInfo} className="info-icon" />
                    <span>
                      Check the items you want to print
                    </span>{' '}
                  </div>
                </div>
              </div>
              <div id="documentContainer">
                {(() => {
                  const elementsToRender = [];
                  let leftColumn = [];
                  let rightColumn = [];
                  let currentStackNo = null;

                  questionsData?.forEach((item, index) => {
                    const hasPosition = item.position === 'left' || item.position === 'right';

                    if (printOptions?.twoColumn && hasPosition) {
                      if (!currentStackNo && item.stackNo) {
                        currentStackNo = item.stackNo;
                      }
                      
                      if (item.position === 'left') leftColumn.push(item);
                      else rightColumn.push(item);
                      
                      const next = questionsData[index + 1];
                      const nextStackNo = next?.stackNo;
                      if (item.isEndStack) {
                        const stackNoForRender = currentStackNo;
                        elementsToRender.push(
                          <div key={`stack_${stackNoForRender ?? index}`} className={`stackBlock d-flex row mb-2 ${stackNoForRender > 1 ? 'page-break-before' : ''}`}>
                            <div className='d-flex align-items-stretch justify-content-between my-2'>
                              <div style={{ width: '49%' }}>
                                {leftColumn.map((leftItem, leftIndex) =>
                                  RenderItem(leftItem, leftIndex)
                                )}
                              </div>
                              <div className='vertical-line'></div>
                              <div style={{ width: '49%' }}>
                                {rightColumn.map((rightItem, rightIndex) => 
                                  RenderItem(rightItem, rightIndex)
                                )}
                              </div>
                            </div>
                          </div>
                        );
                        leftColumn = [];
                        rightColumn = [];
                        currentStackNo = nextStackNo ?? null;
                      }
                    } else {
                      elementsToRender.push(
                        <div key={item.id} id={`question_${item.id}`} className="my-2">
                          {RenderItem(item, index)}
                        </div>
                      );
                    }
                  });
                      
                  return elementsToRender;
                })()}
              </div>
              {duplicateStacks && duplicateStacks.length > 0 && !hideDuplicate &&
              <div id='duplicate-item-div' ref={duplicateDivRef}>
                <div id="dstack" className="d-flex row mb-5">
                  <p className="d-flex justify-content-center fw-bold line">Duplicate Stack</p>
                  <div className="d-flex gap-3">
                    <div style={{ flex: 1 }}>
                      {duplicateStacks.map((item, index) => (
                        <div id={`dstack_itemId_${item.questionId}`} key={`dstack_itemId_${item.questionId}`} className={`dstack_itemId_${item.questionId}`}>
                          {RenderItem(item, index, true)}
                        </div>
                      ))}
                    </div>
                    <div style={{ flex: 1 }} />
                  </div>
                </div>
              </div>
              }

              {printOptions?.showAnswerKey &&
                  <div className='answerkey-content mt-4 page-break-before'>
                    <h4>Answer Key For : {setName}</h4>
                    <div className='d-block'>
                      {questionsData.map((item, index) => (
                        <div key={`questionDetails${index}`}>
                          <div className="d-flex gap-2">
                            <div className='mr-2' style={{minWidth:'60px'}}><h5>{`Q${index + 1} :  `}</h5></div>
                            <div>
                              {item.type === 'MC' &&
                                item.question?.mcAnswer?.map((answer, indexValue) => {
                                  if (answer.isCorrect === 1) return indexValue + 1;
                                })}
                              {item.type === 'CR' && (
                                <>
                                  <span>Constructed Response:</span>
                                  <div dangerouslySetInnerHTML={{ __html: fixContentAlignment(fixSampleAnswerContent(item.question?.sampleAnswer), item.typeId) }}></div>
                                </>
                              )}
                              {item.type === 'FillIn' &&
                                  <div dangerouslySetInnerHTML={{ __html: fixContentAlignment(item.question?.fillInAnswers?.join(', '), item.typeId) }}></div>
                              }
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
              }
            </div>
          </div>
        }
      </div>
    </div>
  );
};

QuestionPrintContainer.propTypes = {
  setName: PropTypes.string,
  displayTopValue: PropTypes.string,
  fontStyleClass: PropTypes.string,
  printOptions: PropTypes.object,
  isLoading: PropTypes.bool,
  teacherName: PropTypes.string,
  questionsData: PropTypes.array,
  handleQuestionsSelect: PropTypes.func,
  handleClearAll: PropTypes.func,
  setContentHeights: PropTypes.func,
  hideDuplicate: PropTypes.bool
};

export default QuestionPrintContainer;